// Pedidos Service - Realtime Database operations
import {
  ref,
  get,
  query,
  orderByChild,
  startAt,
  endAt,
  limitToLast,
  remove,
  update,
  onValue,
  off,
} from 'firebase/database'
import { database } from './firebase'
import {
  HIGH_IMPACT_DELETIONS_DISABLED,
  getHighImpactDeletionBlock,
} from '@/utils/highImpactDeletionGuard'
import { normalizeScheduledDelivery } from '@/utils/pedidos/scheduledDelivery'

const PEDIDOS_BASE_PATH = 'projects/proj_tCJWQHSHNf7WoMu7r64pUJ/data/Pedidos'

/**
 * Map a raw RTDB pedido into the list-item model used by table/cards.
 * Single source for list mapping (incl. scheduledDelivery enrichment).
 * @param {string} pedidoKey
 * @param {Object} pedido
 * @returns {Object}
 */
export const mapPedidoToListItem = (pedidoKey, pedido = {}) => {
  const scheduledDelivery = pedido.scheduledDelivery || null
  const scheduledDeliveryInfo = normalizeScheduledDelivery(scheduledDelivery)

  return {
    pedidoKey,
    folio: pedido.Folio || '',
    cliente: pedido['Cliente name'] || '',
    division: pedido.division || pedido.Division || '',
    tipoEntrega: pedido.recogerTienda === true ? 'pickup' : 'domicilio',
    envio: pedido.Envio ?? 0,
    subtotal: pedido.Subtotal ?? 0,
    total: pedido.Total ?? 0,
    tipoPago: pedido.Pago ?? '',
    status: pedido.Status ?? '',
    timeSolicitud: pedido.TimeSolicitud || 0,
    origen: pedido.Origen || '',
    scheduledDelivery,
    scheduledDeliveryInfo,
    isScheduled: scheduledDeliveryInfo.scheduled,
    rawData: pedido,
  }
}

/**
 * Get pedidos by date range
 * @param {number} startTimestamp - Start timestamp in milliseconds
 * @param {number} endTimestamp - End timestamp in milliseconds
 * @returns {Promise<Object>} Pedidos data
 */
export const getPedidosByDateRange = async (startTimestamp, endTimestamp) => {
  try {
    const pedidosRef = ref(database, PEDIDOS_BASE_PATH)

    // Query with index on TimeSolicitud
    const q = query(
      pedidosRef,
      orderByChild('TimeSolicitud'),
      startAt(startTimestamp),
      endAt(endTimestamp)
    )

    const snapshot = await get(q)

    if (snapshot.exists()) {
      const pedidosData = snapshot.val()

      const pedidosArray = Object.keys(pedidosData).map(pedidoKey =>
        mapPedidoToListItem(pedidoKey, pedidosData[pedidoKey])
      )

      // Sort by date descending (most recent first)
      pedidosArray.sort((a, b) => b.timeSolicitud - a.timeSolicitud)

      return {
        success: true,
        data: pedidosArray,
      }
    } else {
      return {
        success: true,
        data: [],
      }
    }
  } catch (error) {
    console.error('Error in getPedidosByDateRange:', error)
    return {
      success: false,
      error: error.message || 'Error al obtener pedidos',
      data: [],
    }
  }
}

/**
 * Get aggregated metrics for pedidos within a date range
 * @param {number} startTimestamp - Start timestamp in milliseconds
 * @param {number} endTimestamp - End timestamp in milliseconds
 * @returns {Promise<Object>} Metrics data
 */
export const getPedidosMetrics = async (startTimestamp, endTimestamp) => {
  try {
    const pedidosRef = ref(database, PEDIDOS_BASE_PATH)

    const q = query(
      pedidosRef,
      orderByChild('TimeSolicitud'),
      startAt(startTimestamp),
      endAt(endTimestamp)
    )

    const snapshot = await get(q)

    const baseMetrics = {
      totalPedidos: 0,
      totalPedidosActivos: 0,
      totalVentas: 0,
      pedidosConcluidos: 0,
    }

    if (!snapshot.exists()) {
      return {
        success: true,
        data: baseMetrics,
      }
    }

    const pedidosData = snapshot.val()

    Object.keys(pedidosData).forEach(pedidoKey => {
      const pedido = pedidosData[pedidoKey] || {}

      const status = String(pedido.Status || '').toLowerCase()

      baseMetrics.totalPedidos += 1

      const isCancelled = status.includes('cancel')

      if (!isCancelled) {
        baseMetrics.totalPedidosActivos += 1
      }

      if (status.includes('concluido')) {
        baseMetrics.pedidosConcluidos += 1
      }

      if (!isCancelled) {
        const total = parseFloat(pedido.Total ?? pedido.total ?? 0) || 0

        if (!Number.isNaN(total)) {
          baseMetrics.totalVentas += total
        }
      }
    })

    baseMetrics.totalVentas = Math.round(baseMetrics.totalVentas * 100) / 100

    return {
      success: true,
      data: baseMetrics,
    }
  } catch (error) {
    console.error('Error in getPedidosMetrics:', error)
    return {
      success: false,
      error: error.message || 'Error al obtener métricas de pedidos',
      data: {
        totalPedidos: 0,
        totalPedidosActivos: 0,
        totalVentas: 0,
        pedidosConcluidos: 0,
      },
    }
  }
}

/**
 * Listen to pedidos by date range in real-time
 * @param {number} startTimestamp - Start timestamp in milliseconds
 * @param {number} endTimestamp - End timestamp in milliseconds
 * @param {Function} callback - Callback function that receives the pedidos array
 * @returns {Function} Unsubscribe function to stop listening
 */
export const listenPedidosByDateRange = (startTimestamp, endTimestamp, callback) => {
  try {
    if (!startTimestamp || !endTimestamp) {
      console.warn('listenPedidosByDateRange: startTimestamp and endTimestamp are required')
      return () => {} // Return empty unsubscribe function
    }

    const pedidosRef = ref(database, PEDIDOS_BASE_PATH)

    // Query with index on TimeSolicitud
    const q = query(
      pedidosRef,
      orderByChild('TimeSolicitud'),
      startAt(startTimestamp),
      endAt(endTimestamp)
    )

    // Set up real-time listener
    const unsubscribe = onValue(
      q,
      snapshot => {
        if (snapshot.exists()) {
          const pedidosData = snapshot.val()

          const pedidosArray = Object.keys(pedidosData).map(pedidoKey =>
            mapPedidoToListItem(pedidoKey, pedidosData[pedidoKey])
          )

          // Sort by date descending (most recent first)
          pedidosArray.sort((a, b) => b.timeSolicitud - a.timeSolicitud)

          // Call callback with success and data
          callback({
            success: true,
            data: pedidosArray,
          })
        } else {
          // No data found
          callback({
            success: true,
            data: [],
          })
        }
      },
      error => {
        console.error('Error in listenPedidosByDateRange:', error)
        callback({
          success: false,
          error: error.message || 'Error al escuchar cambios en pedidos',
          data: [],
        })
      }
    )

    // Return unsubscribe function
    return unsubscribe
  } catch (error) {
    console.error('Error setting up listener in listenPedidosByDateRange:', error)
    callback({
      success: false,
      error: error.message || 'Error al configurar listener de pedidos',
      data: [],
    })
    return () => {} // Return empty unsubscribe function
  }
}

/**
 * Get pedido by folio
 * @param {string} folio - Pedido folio
 * @returns {Promise<Object>} Pedido data
 */
export const getPedidoByFolio = async folio => {
  try {
    // First, we need to find the pedido by Folio
    // Since Folio is not indexed, we'll search through all pedidos
    // For better performance, consider adding an index on Folio in Firebase
    const pedidosRef = ref(database, PEDIDOS_BASE_PATH)
    const snapshot = await get(pedidosRef)

    if (!snapshot.exists()) {
      return {
        success: false,
        error: 'Pedido no encontrado',
        data: null,
      }
    }

    const pedidosData = snapshot.val()

    // Find pedido with matching Folio
    let foundPedido = null
    let foundKey = null

    for (const key in pedidosData) {
      if (pedidosData[key].Folio === folio) {
        foundPedido = pedidosData[key]
        foundKey = key
        break
      }
    }

    if (!foundPedido) {
      return {
        success: false,
        error: 'Pedido no encontrado',
        data: null,
      }
    }

    return {
      success: true,
      data: {
        pedidoKey: foundKey,
        ...foundPedido,
      },
    }
  } catch (error) {
    console.error('Error in getPedidoByFolio:', error)
    return {
      success: false,
      error: error.message || 'Error al obtener pedido',
      data: null,
    }
  }
}

/**
 * Get pedido by pedido key (e.g., "Pedido-1695584107206")
 * This is more efficient than searching by Folio
 * @param {string} pedidoKey - Pedido key
 * @returns {Promise<Object>} Pedido data
 */
export const getPedidoByKey = async pedidoKey => {
  try {
    const pedidoRef = ref(database, `${PEDIDOS_BASE_PATH}/${pedidoKey}`)
    const snapshot = await get(pedidoRef)

    if (snapshot.exists()) {
      return {
        success: true,
        data: {
          pedidoKey,
          ...snapshot.val(),
        },
      }
    } else {
      return {
        success: false,
        error: 'Pedido no encontrado',
        data: null,
      }
    }
  } catch (error) {
    console.error('Error in getPedidoByKey:', error)
    return {
      success: false,
      error: error.message || 'Error al obtener pedido',
      data: null,
    }
  }
}

/**
 * Get last N pedidos ordered by TimeSolicitud descending
 * @param {number} limit - Number of pedidos to return (default: 5)
 * @returns {Promise<Object>} Pedidos data
 */
export const getLastPedidos = async (limit = 5) => {
  try {
    const pedidosRef = ref(database, PEDIDOS_BASE_PATH)

    // Query ordered by TimeSolicitud and get last N records
    // Note: Firebase orders ascending, so limitToLast gets the highest values
    const q = query(pedidosRef, orderByChild('TimeSolicitud'), limitToLast(limit))

    const snapshot = await get(q)

    if (snapshot.exists()) {
      const pedidosData = snapshot.val()

      const pedidosArray = Object.keys(pedidosData).map(pedidoKey =>
        mapPedidoToListItem(pedidoKey, pedidosData[pedidoKey])
      )

      // Sort by TimeSolicitud descending (most recent first)
      // limitToLast gives us ascending order, so we reverse it
      pedidosArray.sort((a, b) => b.timeSolicitud - a.timeSolicitud)

      return {
        success: true,
        data: pedidosArray,
      }
    } else {
      return {
        success: true,
        data: [],
      }
    }
  } catch (error) {
    console.error('Error in getLastPedidos:', error)
    return {
      success: false,
      error: error.message || 'Error al obtener pedidos',
      data: [],
    }
  }
}

/**
 * Update pedido status and corresponding timestamp
 * @param {string} pedidoKey - Pedido key (e.g., "Pedido-1695584107206")
 * @param {string} newStatus - New status (e.g., "En proceso", "En ruta", "Concluido", "Cancelado")
 * @param {string} motivoCancelacion - Optional cancellation reason (required if status is Cancelado)
 * @param {Object} pickingTicket - Optional picking ticket data (guideNumber, total, ticketNumber)
 * @returns {Promise<Object>} Result of update operation
 */
export const updatePedidoStatus = async (
  pedidoKey,
  newStatus,
  motivoCancelacion = null,
  pickingTicket = null
) => {
  try {
    if (!pedidoKey) {
      return {
        success: false,
        error: 'Key del pedido es requerido',
      }
    }

    if (!newStatus) {
      return {
        success: false,
        error: 'Nuevo estado es requerido',
      }
    }

    const pedidoRef = ref(database, `${PEDIDOS_BASE_PATH}/${pedidoKey}`)
    const timestamp = Date.now()

    // Prepare update data
    const updateData = {
      Status: newStatus,
      updatedAt: timestamp,
    }

    // Add picking ticket data if provided
    if (pickingTicket) {
      updateData.pickingTicket = {
        guideNumber: pickingTicket.guideNumber,
        total: pickingTicket.total,
        ticketNumber: pickingTicket.ticketNumber,
        createdAt: timestamp,
      }
    }

    // Add corresponding timestamp based on status
    const statusLower = newStatus.toLowerCase()
    if (statusLower.includes('proceso')) {
      updateData.TimeEnProceso = timestamp
    } else if (statusLower.includes('listo') && statusLower.includes('pickup')) {
      updateData.TimeListoParaPickup = timestamp
    } else if (statusLower.includes('ruta')) {
      updateData.TimeEnRuta = timestamp
    } else if (statusLower.includes('concluido')) {
      updateData.TimeConcluido = timestamp
    } else if (statusLower.includes('cancelado')) {
      updateData.TimeCancelado = timestamp
      if (motivoCancelacion) {
        updateData.motivoCancelacion = motivoCancelacion
      }
    }

    await update(pedidoRef, updateData)

    return {
      success: true,
      message: 'Estado del pedido actualizado correctamente',
      data: updateData,
    }
  } catch (error) {
    console.error('Error in updatePedidoStatus:', error)
    return {
      success: false,
      error: error.message || 'Error al actualizar estado del pedido',
    }
  }
}

/**
 * Save ticket suspendido reference for a pedido
 * @param {string} pedidoKey - Pedido key (e.g., "Pedido-1695584107206")
 * @param {{ filename: string, ftpPath?: string }} ticketInfo - Ticket information returned by the API
 * @returns {Promise<Object>} Result of update operation
 */
export const saveTicketSuspendido = async (pedidoKey, ticketInfo) => {
  try {
    if (!pedidoKey) {
      return {
        success: false,
        error: 'Key del pedido es requerido',
      }
    }

    if (!ticketInfo || !ticketInfo.filename) {
      return {
        success: false,
        error: 'El nombre del ticket es requerido',
      }
    }

    const filename = String(ticketInfo.filename).trim()
    if (!filename) {
      return {
        success: false,
        error: 'El nombre del ticket es requerido',
      }
    }

    const ticketData = {
      filename,
      ftpPath: ticketInfo.ftpPath ? String(ticketInfo.ftpPath).trim() : '',
      generatedAt: Date.now(),
    }

    const pedidoRef = ref(database, `${PEDIDOS_BASE_PATH}/${pedidoKey}`)
    await update(pedidoRef, { ticketSuspendido: ticketData })

    return {
      success: true,
      message: 'Ticket suspendido guardado correctamente',
      data: ticketData,
    }
  } catch (error) {
    console.error('Error in saveTicketSuspendido:', error)
    return {
      success: false,
      error: error.message || 'Error al guardar ticket suspendido',
    }
  }
}

/**
 * Clear ticket suspendido metadata for a pedido.
 * @param {string} pedidoKey
 * @returns {Promise<{ success: boolean, error?: string }>}
 */
export const clearTicketSuspendido = async pedidoKey => {
  try {
    if (!pedidoKey) {
      return {
        success: false,
        error: 'Key del pedido es requerido',
      }
    }

    const pedidoRef = ref(database, `${PEDIDOS_BASE_PATH}/${pedidoKey}`)
    await update(pedidoRef, { ticketSuspendido: null })

    return {
      success: true,
      message: 'Ticket suspendido limpiado correctamente',
    }
  } catch (error) {
    console.error('Error in clearTicketSuspendido:', error)
    return {
      success: false,
      error: error.message || 'Error al limpiar ticket suspendido',
    }
  }
}

/**
 * Concluir pedido - Update status to "Concluido" and set missing timestamps based on delivery type
 * @param {string} pedidoKey - Pedido key (e.g., "Pedido-1695584107206")
 * @returns {Promise<Object>} Result of update operation
 */
export const concluirPedido = async pedidoKey => {
  try {
    if (!pedidoKey) {
      return {
        success: false,
        error: 'Key del pedido es requerido',
      }
    }

    // First, get the current pedido to check tipo de entrega
    const pedidoResult = await getPedidoByKey(pedidoKey)
    if (!pedidoResult.success || !pedidoResult.data) {
      return {
        success: false,
        error: 'Pedido no encontrado',
      }
    }

    const pedido = pedidoResult.data
    const timestamp = Date.now()
    const isPickup = pedido.recogerTienda === true

    // Prepare update data
    const updateData = {
      Status: 'Concluido',
      updatedAt: timestamp,
    }

    // Set timestamps based on delivery type
    // For Pickup: TimeEnProceso, TimeListoParaPickup, TimeConcluido
    // For Delivery: TimeEnProceso, TimeListoParaPickup, TimeEnRuta, TimeConcluido
    // Only set if they don't already exist

    // TimeEnProceso - always needed
    if (!pedido.TimeEnProceso) {
      updateData.TimeEnProceso = timestamp
    }

    // TimeListoParaPickup - always needed
    if (!pedido.TimeListoParaPickup) {
      updateData.TimeListoParaPickup = timestamp
    }

    // TimeEnRuta - only for Delivery
    if (!isPickup && !pedido.TimeEnRuta) {
      updateData.TimeEnRuta = timestamp
    }

    // TimeConcluido - always set (this is the current action)
    updateData.TimeConcluido = timestamp

    const pedidoRef = ref(database, `${PEDIDOS_BASE_PATH}/${pedidoKey}`)
    await update(pedidoRef, updateData)

    return {
      success: true,
      message: 'Pedido concluido correctamente',
      data: updateData,
    }
  } catch (error) {
    console.error('Error in concluirPedido:', error)
    return {
      success: false,
      error: error.message || 'Error al concluir el pedido',
    }
  }
}

/**
 * Delete pedido by pedido key
 * @param {string} pedidoKey - Pedido key (e.g., "Pedido-1695584107206")
 * @returns {Promise<Object>} Result of delete operation
 */
export const deletePedido = async pedidoKey => {
  try {
    if (HIGH_IMPACT_DELETIONS_DISABLED) {
      return getHighImpactDeletionBlock('eliminar pedido')
    }

    if (!pedidoKey) {
      return {
        success: false,
        error: 'Key del pedido es requerido',
      }
    }

    const pedidoRef = ref(database, `${PEDIDOS_BASE_PATH}/${pedidoKey}`)
    await remove(pedidoRef)

    return {
      success: true,
      message: 'Pedido eliminado correctamente',
    }
  } catch (error) {
    console.error('Error in deletePedido:', error)
    return {
      success: false,
      error: error.message || 'Error al eliminar pedido',
    }
  }
}

/**
 * Update ProductosRecolectados for a specific product in a pedido
 * @param {string} pedidoKey - Pedido key (e.g., "Pedido-1695584107206")
 * @param {string} codigo - Product code
 * @param {Object} pickingData - Picking data to save
 * @returns {Promise<Object>} Result of update operation
 */
export const updateProductoRecolectado = async (pedidoKey, codigo, pickingData) => {
  try {
    if (!pedidoKey) {
      return {
        success: false,
        error: 'Key del pedido es requerido',
      }
    }

    if (!codigo) {
      return {
        success: false,
        error: 'Código del producto es requerido',
      }
    }

    const productoRecolectadoRef = ref(
      database,
      `${PEDIDOS_BASE_PATH}/${pedidoKey}/ProductosRecolectados/${codigo}`
    )

    // Use update to merge with existing data if any
    await update(productoRecolectadoRef, pickingData)

    return {
      success: true,
      message: 'Producto recolectado actualizado correctamente',
    }
  } catch (error) {
    console.error('Error in updateProductoRecolectado:', error)
    return {
      success: false,
      error: error.message || 'Error al actualizar producto recolectado',
    }
  }
}

/**
 * Elimina un producto de ProductosRecolectados (vuelve a pendiente en UI tras refresh).
 * @param {string} pedidoKey
 * @param {string} codigo - Clave del producto (CODIGO)
 * @returns {Promise<{ success: boolean, error?: string }>}
 */
export const removeProductoRecolectado = async (pedidoKey, codigo) => {
  try {
    if (!pedidoKey) {
      return { success: false, error: 'Key del pedido es requerido' }
    }
    if (!codigo) {
      return { success: false, error: 'Código del producto es requerido' }
    }
    const codigoString = String(codigo).trim()
    const productoRecolectadoRef = ref(
      database,
      `${PEDIDOS_BASE_PATH}/${pedidoKey}/ProductosRecolectados/${codigoString}`
    )
    await remove(productoRecolectadoRef)
    return { success: true }
  } catch (error) {
    console.error('Error in removeProductoRecolectado:', error)
    return {
      success: false,
      error: error.message || 'Error al eliminar producto recolectado',
    }
  }
}

/**
 * Recalculate pedido totals based on ProductosRecolectados
 * For ST products: newTotal = pickedQuantity * PrecioUnitario
 * @param {string} pedidoKey - Pedido key
 * @returns {Promise<Object>} Result of update operation
 */
export const recalculatePedidoTotal = async pedidoKey => {
  try {
    if (!pedidoKey) {
      return {
        success: false,
        error: 'Key del pedido es requerido',
      }
    }

    // Get pedido data
    const pedidoRef = ref(database, `${PEDIDOS_BASE_PATH}/${pedidoKey}`)
    const snapshot = await get(pedidoRef)

    if (!snapshot.exists()) {
      return {
        success: false,
        error: 'Pedido no encontrado',
      }
    }

    const pedido = snapshot.val()
    const productos = pedido.Productos || {}
    const productosRecolectados = pedido.ProductosRecolectados || {}
    const envio = parseFloat(pedido.Envio || 0) || 0
    const metodoPago = (pedido.Pago || '').toLowerCase()

    // Check if payment was made online (visa, mastercard, american_express)
    const esPagoEnLinea =
      metodoPago === 'visa' || metodoPago === 'mastercard' || metodoPago === 'american_express'

    // Get original total paid (only for online payments)
    // Only save it the first time, don't overwrite if it already exists
    let totalPagadoOriginal = pedido.totalPagadoOriginal
    if (esPagoEnLinea && !totalPagadoOriginal) {
      // First time recalculating, save the original total that was paid
      totalPagadoOriginal = parseFloat(pedido.Total || 0) || 0
    }

    // Calculate subtotal surtido based ONLY on pickedTotalPrice from ProductosRecolectados
    // Only sum products that have been processed (picked or marked as not_picked)
    // Products not yet processed are NOT included in the surtido total
    // This preserves original Subtotal and Total in Productos collection
    let subtotalSurtido = 0

    // Only iterate through products that have been processed (exist in ProductosRecolectados)
    Object.keys(productosRecolectados).forEach(codigo => {
      const recolectado = productosRecolectados[codigo]

      // Sum pickedTotalPrice from ProductosRecolectados (even if it's 0 for not_picked products)
      if (
        recolectado &&
        recolectado.pickedTotalPrice !== undefined &&
        recolectado.pickedTotalPrice !== null
      ) {
        subtotalSurtido += parseFloat(recolectado.pickedTotalPrice) || 0
      }
    })

    // Calculate total surtido (SubtotalSurtido + Envio)
    // Note: Envio is included in both totals
    const totalSurtido = subtotalSurtido + envio

    // Calculate refund amount (only for online payments when total surtido is less than original)
    let dineroDevolucion = 0
    const totalOriginal = parseFloat(pedido.Total || 0) || 0
    if (esPagoEnLinea && totalPagadoOriginal && totalSurtido < totalPagadoOriginal) {
      dineroDevolucion = totalPagadoOriginal - totalSurtido
    }

    // Update pedido with surtido totals (preserves original Subtotal and Total)
    const updateData = {
      SubtotalSurtido: subtotalSurtido,
      TotalSurtido: totalSurtido,
      totalsUpdatedAt: Date.now(),
    }

    // Add online payment tracking fields only for online payments
    if (esPagoEnLinea) {
      if (totalPagadoOriginal) {
        updateData.totalPagadoOriginal = totalPagadoOriginal
      }
      updateData.dineroDevolucion = dineroDevolucion
    }

    await update(pedidoRef, updateData)

    return {
      success: true,
      message: 'Totales surtidos del pedido recalculados correctamente',
      data: {
        SubtotalSurtido: subtotalSurtido,
        TotalSurtido: totalSurtido,
      },
    }
  } catch (error) {
    console.error('Error in recalculatePedidoTotal:', error)
    return {
      success: false,
      error: error.message || 'Error al recalcular total del pedido',
    }
  }
}

/**
 * Add a product to a pedido's Productos collection
 * @param {string} pedidoKey - Pedido key
 * @param {Object} productData - Product data from Algolia search result
 * @param {number} store - Store division ID
 * @param {number} cantidad - Quantity to add (default: 1). For piece mode, this is the number of pieces.
 * @param {boolean} sellByPiece - If true, product is being sold by piece (for KG products with PESO_PIEZA and PRECIO_PIEZA)
 * @returns {Promise<Object>} Result of add operation
 */
export const addProductToPedido = async (
  pedidoKey,
  productData,
  store,
  cantidad = 1,
  sellByPiece = false
) => {
  try {
    if (!pedidoKey) {
      return {
        success: false,
        error: 'Key del pedido es requerido',
      }
    }

    if (!productData || !productData.CODIGO) {
      return {
        success: false,
        error: 'Datos del producto inválidos o código faltante',
      }
    }

    if (!store) {
      return {
        success: false,
        error: 'ID de tienda es requerido',
      }
    }

    // Get pedido to verify it exists
    const pedidoRef = ref(database, `${PEDIDOS_BASE_PATH}/${pedidoKey}`)
    const snapshot = await get(pedidoRef)

    if (!snapshot.exists()) {
      return {
        success: false,
        error: 'Pedido no encontrado',
      }
    }

    const pedido = snapshot.val()

    // Dynamic keys based on store
    const priceKey = `PRECIO_${store}`
    const offerTypeKey = `TIPOOFERTA_${store}`
    const offerPriceKey = `PRECIOOFERTA_${store}`
    const precioPiezaKey = `PRECIO_PIEZA_${store}`

    // Determine price unit based on sell mode
    let precioUnitario

    const offerTypeValue = productData[offerTypeKey] || 0
    const offerPriceValue = parseFloat(productData[offerPriceKey] || 0) || 0
    const regularPriceValue = parseFloat(productData[priceKey] || 0) || 0

    let precioAlgolia =
      offerTypeValue === 101 && offerPriceValue > 0 ? offerPriceValue : regularPriceValue

    if (sellByPiece && productData.UNIDAD === 'KG') {
      // For piece mode, use PRECIO_PIEZA_$sucursal
      precioUnitario = parseFloat(productData[precioPiezaKey] || 0) || 0
    } else {
      // Normal mode: if offer type is 101 and offer price > 0, use offer price, else use regular price
      precioUnitario = precioAlgolia
    }

    // Round cantidad to maximum 2 decimal places for CantidadGRPZ
    const cantidadRedondeada = Math.round(cantidad * 100) / 100

    // Calculate total using the rounded cantidad to maintain consistency
    const total = cantidadRedondeada * precioUnitario

    // Get offer type for display (only relevant for non-piece mode)
    const offerType = sellByPiece ? 0 : offerTypeValue

    // Build product object matching the exact structure from existing products
    const codigo = String(productData.CODIGO)
    // Format Cantidad string:
    // - For piece mode with KG: show as "XPZ"
    // - For KG products with cantidad < 1: convert to grams and show as "XGR"
    // - For KG products with cantidad >= 1: show as "XKG"
    // - For non-KG products: show as "XPZ"
    let cantidadString
    if (sellByPiece && productData.UNIDAD === 'KG') {
      cantidadString = `${cantidadRedondeada}PZ`
    } else if (productData.UNIDAD === 'KG') {
      if (cantidadRedondeada < 1) {
        // Convert to grams (multiply by 1000) and round to avoid decimals
        const gramos = Math.round(cantidadRedondeada * 1000)
        cantidadString = `${gramos}GR`
      } else {
        cantidadString = `${cantidadRedondeada}KG`
      }
    } else {
      cantidadString = `${cantidadRedondeada}PZ`
    }

    const nuevoProducto = {
      Ahorro: productData.AHORRO || 0,
      CATEGORIAAPP: productData.CATEGORIAAPP || '',
      Cantidad: cantidadString,
      CantidadGRPZ: cantidadRedondeada,
      Codigo: codigo,
      Comentarios: '',
      Existencia: productData[`EXISTENCIA_${store}`] || 0,
      Imagen: productData.URLS512 || productData.URLS256 || productData.URLS128 || '',
      Nombre: productData.DESCRIPCION || productData.Nombre || '',
      PrecioUnitario: precioUnitario,
      TipoOferta: offerType,
      Total: total,
      Unidad: productData.UNIDAD || 'ST',
      id: codigo,
      UnidadAlgolia: productData.UNIDAD || 'ST',
      PrecioAlgolia: precioAlgolia,
      EsSustituto: true, // Mark as substitute (optional)
    }

    // Update only the specific product in Productos using update() with path
    const updateData = {}
    updateData[`Productos/${codigo}`] = nuevoProducto

    await update(pedidoRef, updateData)

    // Recalculate order totals
    await recalculatePedidoTotal(pedidoKey)

    return {
      success: true,
      message: 'Producto agregado al pedido correctamente',
      data: nuevoProducto,
    }
  } catch (error) {
    console.error('Error in addProductToPedido:', error)
    return {
      success: false,
      error: error.message || 'Error al agregar producto al pedido',
    }
  }
}

/**
 * Remove a product from a pedido's Productos collection
 * @param {string} pedidoKey - Pedido key
 * @param {string} codigo - Product code (CODIGO)
 * @returns {Promise<Object>} Result of remove operation
 */
export const removeProductFromPedido = async (pedidoKey, codigo) => {
  try {
    if (!pedidoKey) {
      return {
        success: false,
        error: 'Key del pedido es requerido',
      }
    }

    if (!codigo) {
      return {
        success: false,
        error: 'Código del producto es requerido',
      }
    }

    // Get pedido to verify it exists
    const pedidoRef = ref(database, `${PEDIDOS_BASE_PATH}/${pedidoKey}`)
    const snapshot = await get(pedidoRef)

    if (!snapshot.exists()) {
      return {
        success: false,
        error: 'Pedido no encontrado',
      }
    }

    const codigoString = String(codigo)

    // Remove product from Productos using update() with null to delete
    const updateData = {}
    updateData[`Productos/${codigoString}`] = null

    // Also remove from ProductosRecolectados if it exists
    updateData[`ProductosRecolectados/${codigoString}`] = null

    await update(pedidoRef, updateData)

    // Recalculate order totals
    await recalculatePedidoTotal(pedidoKey)

    return {
      success: true,
      message: 'Producto eliminado del pedido correctamente',
    }
  } catch (error) {
    console.error('Error in removeProductFromPedido:', error)
    return {
      success: false,
      error: error.message || 'Error al eliminar producto del pedido',
    }
  }
}
